// Therapy-type configuration. Each type maps to display labels for the
// Evaluation and Daily Notes tabs. The underlying backend storage uses fixed
// slot keys (eval_1..4, daily_1..3) so the same plan record can be relabelled
// without migration if a therapist changes type.

export const THERAPY_TYPES = {
  physiotherapy: {
    label: "Physiotherapy",
    description: "MSK, post-op, neuro and general physio.",
    evaluation: ["Assessment", "Objective", "Goals", "History"],
    daily: ["Objective", "Assessment", "History"],
    goalsSlotIndex: 2, // 0-indexed slot that gets AI-auto-generated goals
    objectiveSlotIndex: 1,
  },
  sports: {
    label: "Sports Therapy",
    description: "Athletes, return-to-play, rehab progressions.",
    evaluation: ["Injury Assessment", "Functional Testing", "Rehabilitation Goals", "Injury History"],
    daily: ["Functional Testing", "Session Assessment", "Progress Notes"],
    goalsSlotIndex: 2,
    objectiveSlotIndex: 1,
  },
  chiropractic: {
    label: "Chiropractic",
    description: "SOAP-aligned: subjective, objective, assessment, plan.",
    evaluation: ["Subjective", "Objective", "Assessment", "Plan"],
    daily: ["Objective", "Assessment", "Plan"],
    goalsSlotIndex: 3, // Plan is the goals-equivalent
    objectiveSlotIndex: 1,
  },
  occupational: {
    label: "Occupational Therapy",
    description: "Function, ADLs, return-to-work goals.",
    evaluation: ["Occupational Profile", "Performance Analysis", "Goals", "Background History"],
    daily: ["Performance Observations", "Clinical Assessment", "Progress Update"],
    goalsSlotIndex: 2,
    objectiveSlotIndex: 1,
  },
  osteopathy: {
    label: "Osteopathy",
    description: "Structural assessment + treatment plans.",
    evaluation: ["Presenting Complaint", "Structural Findings", "Treatment Goals", "Medical History"],
    daily: ["Structural Findings", "Treatment Applied", "Response and Progress"],
    goalsSlotIndex: 2,
    objectiveSlotIndex: 1,
  },
  massage: {
    label: "Massage Therapy",
    description: "Soft-tissue, areas treated, client response.",
    evaluation: ["Presenting Complaint", "Physical Assessment", "Treatment Goals", "Health History"],
    daily: ["Areas Treated", "Techniques Applied", "Client Response"],
    goalsSlotIndex: 2,
    objectiveSlotIndex: 1,
  },
  other: {
    label: "Other",
    description: "Default labels — physiotherapy style.",
    evaluation: ["Assessment", "Objective", "Goals", "History"],
    daily: ["Objective", "Assessment", "History"],
    goalsSlotIndex: 2,
    objectiveSlotIndex: 1,
  },
};

// Backend storage uses these slot keys regardless of label
export const EVAL_SLOTS = ["eval_1", "eval_2", "eval_3", "eval_4"];
export const DAILY_SLOTS = ["daily_1", "daily_2", "daily_3"];

// Legacy key map (for plans created before slot model)
const LEGACY_EVAL = ["assessment", "objective", "goals", "history"];
const LEGACY_DAILY = ["objective", "assessment", "history"];

export function getTherapyConfig(type) {
  return THERAPY_TYPES[type] || THERAPY_TYPES.physiotherapy;
}

// Read a slot value from a plan section, handling legacy plans transparently.
export function readSlot(section, slotKey, slotIndex, tab) {
  if (!section) return "";
  if (section[slotKey] !== undefined && section[slotKey] !== null) return section[slotKey] || "";
  const legacy = tab === "evaluation" ? LEGACY_EVAL[slotIndex] : LEGACY_DAILY[slotIndex];
  return (legacy && section[legacy]) || "";
}

export function writeSlot(section, slotKey, value) {
  return { ...section, [slotKey]: value };
}

// Disciplines we currently expose in the UI (onboarding + account change).
// `THERAPY_TYPES` keeps every config for backward-compat reads of historic
// plans / users, but only these show up in selection lists.
const VISIBLE_TYPES = ["physiotherapy", "sports", "chiropractic", "occupational"];

export const THERAPY_OPTIONS = VISIBLE_TYPES.map((k) => ({
  value: k,
  ...THERAPY_TYPES[k],
}));
